import { Experiment } from "@amplitude/experiment-js-client"
import {
  type PropsWithChildren,
  Suspense,
  useEffect,
  useSyncExternalStore,
} from "react"
import { z } from "zod"

import { env } from "../env"
import { getRepositoryName } from "../routing"
import { trackExperimentExposure } from "../services/tracking"
import { useProfile } from "../services/user"

export function AmplitudeExperiments(props: PropsWithChildren) {
  return (
    <>
      <Suspense>
        <BlockingExperiments />
      </Suspense>
      {props.children}
    </>
  )
}

function BlockingExperiments() {
  const { shortId } = useProfile()

  useEffect(() => {
    experiment.start(shortId)
    return experiment.stop
  }, [shortId])

  return null
}

export function usePromptToSliceFlag(args: { active: boolean }) {
  const { active } = args
  const flag = useFeatureFlag("prompt-to-slice", active)
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useAiTranslationFlag(args: { active: boolean }) {
  const { active } = args
  const flag = useFeatureFlag("ai-translation", active)
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useReleasesTabFlag() {
  const flag = useFeatureFlag("pb-releases-tab")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useMigrationReleaseNoModalFlag() {
  const flag = useFeatureFlag("migration-release-no-modal")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useCollaborationRoomFlag() {
  const flag = useFeatureFlag("pb-collaboration-room")
  return z.enum(["on", "off"]).catch("off").parse(flag)
}

export function useSharedOnboardingGuideFlag() {
  const flag = useFeatureFlag("shared-onboarding-new")
  return z
    .enum(["control", "with-shared-onboarding"])
    .catch("control")
    .parse(flag)
}

const awsBedrockConfigSchema = z.object({
  accessKeyId: z.string().min(1),
  secretAccessKey: z.string().min(1),
  region: z.string().min(1),
})

export function useAiSeoMetadataFlag() {
  const variant = experiment.variant("pb-ai-seo-metadata")
  const state = z.enum(["on", "off"]).catch("off").parse(variant.value)

  if (state === "on") {
    return awsBedrockConfigSchema.parse(variant.payload)
  }

  return "off"
}

export function clearExperiments() {
  experiment.stop()
  experiment.clear()
}

/**
 * @param active Evaluating a flag (via calling experiment.variant) also produces an analytics event as a side effect.
 * We sometimes use this behavior to track how many users interacted with a feature once they saw it.
 * To prevent false negatives, we don't want to call experiment.variant if they can't see the feature yet.
 * One such case is Radix-based dialogs, which are always rendered even when not opened.
 */
function useFeatureFlag(flag: string, active = true) {
  return useSyncExternalStore(experiment.subscribe, () =>
    active ? experiment.variant(flag).value : undefined,
  )
}

/**
 * The Amplitude SDK is pretty bad and won't reliably publish new flag values when polling, so we do it ourselves.
 * Using useSyncExternalStore also has the benefit of isolating the rendering of each feature flag.
 */
function createExperimentStore() {
  let pollingId: number | undefined
  let listeners: Array<() => void> = []

  const experiment = Experiment.initialize(
    env.PRISMIC_PAGE_BUILDER_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY,
    {
      serverUrl: new URL(
        "api",
        env.PRISMIC_AMPLITUDE_EXPERIMENT_SERVER_BASE_URL,
      ).href,
      flagsServerUrl: new URL(
        "flag",
        env.PRISMIC_AMPLITUDE_EXPERIMENT_SERVER_BASE_URL,
      ).href,
      exposureTrackingProvider: { track: trackExperimentExposure },
    },
  )

  function start(userId: string) {
    stop()

    async function poll() {
      await experiment.fetch({
        user_id: userId,
        groups: { Repository: [getRepositoryName()] },
      })

      listeners.forEach((listener) => listener())
    }

    void poll()
    pollingId = window.setInterval(poll, 10_000)
  }

  function stop() {
    clearInterval(pollingId)
  }

  function clear() {
    experiment.clear()
  }

  function subscribe(listener: () => void) {
    listeners.push(listener)

    return function unsubscribe() {
      listeners = listeners.filter(
        (otherListener) => otherListener !== listener,
      )
    }
  }

  function variant(flag: string) {
    return experiment.variant(flag)
  }

  return {
    start,
    stop,
    clear,
    subscribe,
    variant,
  }
}

const experiment = createExperimentStore()
